import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';
import { attributes } from '@bb/contracts';
import { Button, ErrorState, StatusPill } from '@bb/ui';
import { ApiError, importErrorsUrl, type ImportColumnMapping, type ImportOptions, type ImportView } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { IMPORT_STATUS } from './ImportsPage.tsx';

type Target = 'ignore' | 'id:external_id' | 'id:alias_label' | 'id:alias_name' | `std:${string}` | 'custom' | 'group';
interface Row {
  column: string;
  target: Target;
  key: string;
  dataType: string;
  groupId: string;
}
const NEW_TYPES = ['string', 'number', 'boolean', 'time', 'array'];
const EDITABLE: Array<ImportView['status']> = ['uploaded', 'validated', 'validation_failed'];
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const count = (n: number | null) => (n === null ? '—' : n.toLocaleString('en-US'));

function toRows(view: ImportView): Row[] {
  const mapping = view.mapping ?? view.suggestedMapping ?? view.headers.map((column) => ({ column, kind: 'ignore' as const }));
  return view.headers.map((column) => {
    const m = mapping.find((x) => x.column === column);
    const base = { column, key: '', dataType: '', groupId: '' };
    if (!m || m.kind === 'ignore') return { ...base, target: 'ignore' };
    if (m.kind === 'identifier') return { ...base, target: `id:${m.field}` };
    if (m.kind === 'standard') return { ...base, target: `std:${m.key}` };
    if (m.kind === 'custom') return { ...base, target: 'custom', key: m.key, dataType: m.dataType ?? '' };
    return { ...base, target: 'group', groupId: m.groupId };
  });
}

function toMapping(rows: Row[], known: Map<string, string>): ImportColumnMapping[] {
  return rows.map((r): ImportColumnMapping => {
    if (r.target === 'ignore') return { column: r.column, kind: 'ignore' };
    if (r.target.startsWith('id:')) return { column: r.column, kind: 'identifier', field: r.target.slice(3) as 'external_id' | 'alias_label' | 'alias_name' };
    if (r.target.startsWith('std:')) return { column: r.column, kind: 'standard', key: r.target.slice(4) };
    if (r.target === 'group') return { column: r.column, kind: 'subscription_group', groupId: r.groupId };
    const key = r.key.trim();
    return { column: r.column, kind: 'custom', key, ...(!known.has(key) && r.dataType ? { dataType: r.dataType as 'string' } : {}) };
  });
}

/** Column → attribute or group, the options, and «Save and run the dry run». */
function MappingEditor({ environmentId, view }: { environmentId: string; view: ImportView }) {
  const { api } = useRouteContext({ from: '/env/$environmentId/audience/import/$importId' });
  const queryClient = useQueryClient();
  const [rows, setRows] = useState(() => toRows(view));
  const [options, setOptions] = useState<ImportOptions>(view.options);
  const customs = useQuery({ queryKey: ['attributes', environmentId, 'import'], queryFn: () => api.listAttributes(environmentId, {}) });
  const groups = useQuery({ queryKey: ['subscription-groups', environmentId, 'active'], queryFn: () => api.listSubscriptionGroups(environmentId, { status: 'active' }) });
  const known = new Map((customs.data?.items ?? []).filter((a) => a.kind === 'custom').map((a) => [a.key, a.dataType]));
  const set = (i: number, patch: Partial<Row>) => setRows((all) => all.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const run = useMutation({
    mutationFn: async () => {
      await api.putImportMapping(environmentId, view.id, { mapping: toMapping(rows, known), options });
      return api.importAction(environmentId, view.id, 'validate');
    },
    onSuccess: (next) => queryClient.setQueryData(['imports', environmentId, view.id], next),
  });
  return (
    <section aria-label="Mapping" className="panel">
      <h2>Map the columns</h2>
      <table className="table" aria-label="Columns">
        <thead>
          <tr>
            <th scope="col">CSV column</th>
            <th scope="col">Maps to</th>
            <th scope="col">Details</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.column}>
              <th scope="row">{r.column}</th>
              <td>
                <select aria-label={`Maps to for ${r.column}`} value={r.target} onChange={(e) => set(i, { target: e.target.value as Target })}>
                  <option value="ignore">Ignore</option>
                  <optgroup label="Identifier">
                    <option value="id:external_id">External ID</option>
                    <option value="id:alias_label">Alias label</option>
                    <option value="id:alias_name">Alias name</option>
                  </optgroup>
                  <optgroup label="Standard attribute">
                    {attributes.STANDARD_ATTRIBUTES.map((a) => (
                      <option key={a.key} value={`std:${a.key}`}>
                        {a.key}
                      </option>
                    ))}
                  </optgroup>
                  <option value="custom">Custom attribute</option>
                  <option value="group">Subscription group</option>
                </select>
              </td>
              <td>
                {r.target === 'custom' && (
                  <>
                    <input aria-label={`Attribute key for ${r.column}`} list="import-custom-keys" value={r.key} onChange={(e) => set(i, { key: e.target.value })} />
                    {r.key.trim() && known.has(r.key.trim()) ? (
                      <span className="hint"> {known.get(r.key.trim())} (registry)</span>
                    ) : (
                      <select aria-label={`Type for ${r.column}`} value={r.dataType} onChange={(e) => set(i, { dataType: e.target.value })}>
                        <option value="">Choose a type…</option>
                        {NEW_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    )}
                  </>
                )}
                {r.target === 'group' && (
                  <select aria-label={`Group for ${r.column}`} value={r.groupId} onChange={(e) => set(i, { groupId: e.target.value })}>
                    <option value="">Choose a group…</option>
                    {(groups.data ?? []).map((g) => (
                      <option key={g.apiId} value={g.apiId}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <datalist id="import-custom-keys">
        {[...known.keys()].map((k) => (
          <option key={k} value={k} />
        ))}
      </datalist>
      <fieldset>
        <legend>Options</legend>
        <label>
          <input type="checkbox" checked={options.createMissing} onChange={(e) => setOptions({ ...options, createMissing: e.target.checked })} /> Create users that do not exist yet
        </label>
        <label>
          <input
            type="checkbox"
            checked={options.allowResubscribe}
            onChange={(e) => setOptions({ createMissing: options.createMissing, allowResubscribe: e.target.checked, ...(e.target.checked && options.resubscribeReason ? { resubscribeReason: options.resubscribeReason } : {}) })}
          />{' '}
          Re-subscribe users who unsubscribed
        </label>
        {options.allowResubscribe && (
          <label>
            Reason (kept in the audit log)
            <textarea value={options.resubscribeReason ?? ''} onChange={(e) => setOptions({ ...options, resubscribeReason: e.target.value })} />
          </label>
        )}
      </fieldset>
      {run.isError && <p role="alert">{errorText(run.error)}</p>}
      <Button variant="primary" onClick={() => run.mutate()} disabled={run.isPending || (options.allowResubscribe && !options.resubscribeReason?.trim())}>
        Save and run the dry run
      </Button>
    </section>
  );
}

function Report({ environmentId, view }: { environmentId: string; view: ImportView }) {
  return (
    <section aria-label="Dry run report" className="panel">
      <h2>{view.rowsApplied ? 'Result' : 'Dry run'}</h2>
      <dl className="facts">
        <dt>Rows</dt>
        <dd>{count(view.totalRows)}</dd>
        <dt>Valid rows</dt>
        <dd>{count(view.validRows)}</dd>
        <dt>Rows with errors</dt>
        <dd>{count(view.errorRows)}</dd>
        <dt>New users</dt>
        <dd>{count(view.newProfilesEstimate)}</dd>
        {view.rowsApplied > 0 && (
          <>
            <dt>Applied</dt>
            <dd>{count(view.rowsApplied)}</dd>
          </>
        )}
      </dl>
      {view.errors.length > 0 && (
        <>
          <table className="table" aria-label="Row errors">
            <thead>
              <tr>
                <th scope="col">Row</th>
                <th scope="col">Column</th>
                <th scope="col">Problem</th>
                <th scope="col">Value</th>
              </tr>
            </thead>
            <tbody>
              {view.errors.map((e) => (
                <tr key={`${e.rowNumber}/${e.column}/${e.code}`}>
                  <td>{e.rowNumber}</td>
                  <td>{e.column ?? '—'}</td>
                  <td>
                    <code>{e.code}</code> {e.message}
                  </td>
                  <td>{e.valuePreview ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <a href={importErrorsUrl(environmentId, view.id)} download>
            Download the error report
          </a>
        </>
      )}
    </section>
  );
}

/**
 * One CSV import (BB-36): mapping and options, the dry run report, and the apply after an explicit confirmation.
 * The page polls while a job runs.
 */
export function ImportPage() {
  const { environmentId, importId } = useParams({ from: '/env/$environmentId/audience/import/$importId' });
  const { api } = useRouteContext({ from: '/env/$environmentId/audience/import/$importId' });
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const [confirming, setConfirming] = useState(false);
  const view = useQuery({
    queryKey: ['imports', environmentId, importId],
    queryFn: () => api.getImport(environmentId, importId),
    refetchInterval: (q) => (q.state.data && ['validating', 'applying'].includes(q.state.data.status) ? 2000 : false),
  });
  const act = useMutation({
    mutationFn: (action: 'apply' | 'cancel') => api.importAction(environmentId, importId, action),
    onSuccess: (next) => {
      setConfirming(false);
      queryClient.setQueryData(['imports', environmentId, importId], next);
      return queryClient.invalidateQueries({ queryKey: ['imports', environmentId], exact: true });
    },
  });
  if (view.isPending) return <p aria-busy="true">Loading import…</p>;
  if (view.isError) return <ErrorState title="The import could not be loaded" message={errorText(view.error)} onRetry={() => void view.refetch()} />;
  const v = view.data;
  const [label, tone] = IMPORT_STATUS[v.status];
  const canEdit = permissions.has('profiles.import');
  const ready = v.status === 'validated' && v.validatedMappingVersion === v.mappingVersion;
  return (
    <section>
      <p>
        <Link to="/env/$environmentId/audience/import" params={{ environmentId }}>
          ← Import users
        </Link>
      </p>
      <h1>
        {v.fileName} <StatusPill tone={tone}>{label}</StatusPill>
      </h1>
      <p className="hint">
        {(v.sizeBytes / 1024).toFixed(0)} KB, {v.headers.length} columns{v.uploadedBy?.email ? `, uploaded by ${v.uploadedBy.email}` : ''}.{v.fileDeletedAt ? ' The file has been deleted; the report stays.' : ''}
      </p>
      {(v.status === 'validating' || v.status === 'applying') && <p aria-busy="true">{v.status === 'validating' ? 'Checking every row…' : `Applying… ${count(v.rowsApplied)} rows so far`}</p>}
      {v.validRows !== null && <Report environmentId={environmentId} view={v} />}
      {canEdit && ready && (
        <section aria-label="Apply" className="panel">
          {confirming ? (
            <>
              <p>
                {count(v.validRows)} rows will update users{v.newProfilesEstimate ? ` and create about ${count(v.newProfilesEstimate)} new ones` : ''}. Rows with errors are skipped. An import cannot be undone.
              </p>
              <Button variant="primary" onClick={() => act.mutate('apply')} disabled={act.isPending}>
                Apply {count(v.validRows)} rows
              </Button>{' '}
              <Button onClick={() => setConfirming(false)}>Back</Button>
            </>
          ) : (
            <Button variant="primary" onClick={() => setConfirming(true)}>
              Apply…
            </Button>
          )}
        </section>
      )}
      {canEdit && EDITABLE.includes(v.status) && <MappingEditor key={`${v.id}/${v.mappingVersion}`} environmentId={environmentId} view={v} />}
      {canEdit && EDITABLE.includes(v.status) && (
        <Button variant="danger" onClick={() => act.mutate('cancel')} disabled={act.isPending}>
          Cancel import
        </Button>
      )}
      {act.isError && <p role="alert">{errorText(act.error)}</p>}
    </section>
  );
}
